import { NO_OPTIONAL_TRACKING, type PrivacyPreferences } from "./consent-state.ts";

export interface AnalyticsClient {
  start(replay: boolean): void;
  stop(): void;
  capture(event: string, properties?: Record<string, unknown>): void;
  captureException(error: Error, properties: Record<string, unknown>): void;
  showDownloadSurvey(): void;
}
/** Drops events until consent and SDK readiness; never queues pre-consent activity. */
export class OptionalAnalytics {
  private preferences: PrivacyPreferences = NO_OPTIONAL_TRACKING;
  private client: AnalyticsClient | null = null;
  private loading: Promise<void> | null = null;
  private loadClient: () => Promise<AnalyticsClient>;
  private clearStorage: () => void;
  constructor(loadClient: () => Promise<AnalyticsClient>, clearStorage: () => void) {
    this.loadClient = loadClient;
    this.clearStorage = clearStorage;
  }
  setConsent(preferences: PrivacyPreferences): Promise<void> {
    this.preferences = { analytics: preferences.analytics, replay: preferences.analytics && preferences.replay };
    if (!this.preferences.analytics) {
      this.client?.stop();
      this.clearStorage();
      return Promise.resolve();
    }
    if (this.client) { this.client.start(this.preferences.replay); return Promise.resolve(); }
    if (!this.loading) {
      this.loading = this.loadClient().then((client) => {
        this.client = client;
        // The SDK import can finish after a visitor has withdrawn consent.
        if (this.preferences.analytics) client.start(this.preferences.replay);
        else { client.stop(); this.clearStorage(); }
      }).catch(() => {
        // An ad blocker or failed SDK download must not break an audio tool.
      }).finally(() => { this.loading = null; });
    }
    return this.loading;
  }
  capture(event: string, properties?: Record<string, unknown>): void {
    if (this.preferences.analytics) this.client?.capture(event, properties);
  }
  captureException(error: Error, properties: Record<string, unknown>): void {
    if (this.preferences.analytics) this.client?.captureException(error, properties);
  }
  showDownloadSurvey(): void {
    if (this.preferences.analytics) this.client?.showDownloadSurvey();
  }
}
